/**
 * The patient record surface: attachments, their upload, the Group and Content
 * they carry, and the patient EMR journal built on them.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const emr = {

  'protocolChoice.primary': "Primary protocol",
  'protocolChoice.choose': "Choose a protocol",
  'protocolChoice.empty': "No active primary protocols",
  'protocolChoice.defaultUnavailable': "No default primary protocol is available. Ask a clinic administrator to configure one or grant protocol selection.",
  'protocolChoice.replace': "Change primary protocol",
  'protocolChoice.replacing': "Replacing…",
  'protocolChoice.confirmReplace': "Discard protocol content and replace",
  'protocolChoice.discardDetail': "All saved and unsaved content of the current primary protocol, including its translations, will be discarded from the current entry. The new protocol starts empty. Diagnoses, additional protocols, services, referrals and other entry data are preserved. The previous protocol remains in history.",
  'protocolChoice.current': "Current protocol",
  'protocolChoice.new': "New protocol",
  'protocolChoice.notPrimary': "This protocol is an additional document. Choose a primary protocol.",
  'protocolChoice.unchanged': "This protocol is already the current primary protocol.",
  'protocolChoice.primaryExists': "This entry already has a primary protocol. Use Change primary protocol.",

  /* The files registry. Its states are FIVE and each says a different thing:
     nothing loaded yet, the read failed, this Patient has no files, no file
     matches the filters, and this page of a cursor is past the end while files
     still match. The last is the one that must never borrow another's words. */
  'file.title': 'Files',
  'file.tableLabel': 'Patient files',
  'file.columnName': 'Name',
  'file.columnDocumentDate': 'Document date',
  'file.columnUploaded': 'Uploaded',
  'file.columnAuthor': 'Author',
  /* Type and size are two columns, not one cell carrying both: a category is
     narrowed by a value list and a size by a range, and one funnel per column
     cannot offer two questions. */
  /* WHERE THE ROW IS IN ITS OWN LIFE. A stored document has arrived and has no
     answer here; an imaging row is transferring, waiting to be reviewed, or
     hosted, and that is the fact a clinician scans the merged table for. */
  'file.columnStatus': 'Status',
  'file.columnSize': 'Size',
  'file.columnLinks': 'Clinic record links',
  'file.columnActions': 'Actions',

  /* EVERY COLUMN BUT THE ACTIONS ASKS ITS OWN QUESTION, in its own header.
     These label the controls inside those panels; the headings above label the
     columns. The two are deliberately different words — one names a fact, the
     other names a bound on it.

     The two date columns name their bounds from their own headings, through
     the shared `table.rangeFrom` — they are civil days in the clinic's own
     zone, which is why they are dates and not instants, and the surface that
     renamed the column renamed its funnel with it. */
  // Kilobytes, because that is the unit the column itself shows — which is why
  // this pair is spelled out where the dates are not. The wire carries bytes
  // and the control converts.
  'file.sizeFrom': 'Size from (KB)',
  'file.sizeTo': 'Size to (KB)',
  // Distinct from the column heading on purpose: one names a value, the other
  // names a control that changes which values are asked for.
  'file.linkStateLabel': 'Filter by record links',
  'file.linkStateAll': 'All files',
  'file.linkStateLinked': 'Linked to a record',
  'file.linkStateUnlinked': 'Not linked',

  /* The registry's one entry point for new documents, and the context panel a
     selected file opens on the right. This names the panel's region, which is
     what tells a reader which panel they are in — the close control inside it
     is `panel.close` and says only what pressing it does.

     "Add", not "Add file": the control lives in the Files section's toolbar,
     under a heading that already says what is being added. */
  'file.selectedFile': 'Selected file',

  'file.loading': 'Loading files',
  'file.unavailable': 'The file list is unavailable',
  // A refusal by name, not an empty registry: an account that may not read a
  // patient's files has not been told the patient has none.
  'file.forbidden': 'You do not have permission to view patient files',
  'file.forbiddenDetail': 'The rest of the patient card is still available.',
  // States only that the Patient has no files. It says nothing about filters,
  // because when one is active this is not the sentence that is true.
  'file.empty': 'This patient has no files',
  'file.filteredEmpty': 'No files match these filters',
  'file.filteredEmptyDetail': 'Reset the filters to see every file for this patient.',
  'file.pageEmpty': 'This page of results is no longer available',
  'file.pageEmptyDetail': 'Files still match. Go back to the first page to see them.',
  'file.firstPage': 'Back to the first page',

  'file.expand': 'Show details for {name}',
  'file.collapse': 'Hide details for {name}',
  'file.download': 'Download {name}',
  'file.preview': 'View',
  'file.previewNamed': 'View {name}',
  'file.previewTitle': 'View {name}',
  'file.previewClose': 'Close',
  'file.previewControls': 'Document viewer controls',
  'file.previewPrevious': 'Previous page',
  'file.previewNext': 'Next page',
  'file.previewPage': 'Page {page} of {count}',
  'file.previewZoomOut': 'Zoom out',
  'file.previewZoomIn': 'Zoom in',
  'file.previewLoading': 'Preparing preview…',
  'file.previewUnavailable': 'The preview could not be opened. The stored file is unchanged.',
  'file.previewCanvas': 'Document page {page}',
  // Names the file, and states plainly that nothing was lost. A failed read of
  // a clinical document must not read as a deletion.
  'file.downloadFailed': '{name} could not be downloaded. The file is unchanged.',

  // What the ticked rows can be downloaded as. One row has one outcome and is
  // a plain button; from two up the two outcomes differ and the menu names
  // both. "Separately" rather than "one by one": what the reader gets is
  // several files, not several presses.
  'file.downloadOne': 'Download the selected file',
  'file.downloadSelected': 'Download {count} files',
  'file.downloadEach': 'Download separately',
  'file.downloadArchive': 'Download as one archive',
  'file.downloadWorking': 'Fetching the selected files…',
  // The run stopped where it failed, so the sentence says so: a bundle missing
  // files nobody was told about is worse than a refusal.
  'file.downloadStopped':
    '{name} could not be downloaded, so the rest were not fetched. The files are unchanged.',
  // Every file arrived and the archive did not, so this names no row.
  'file.downloadArchiveFailed': 'The archive could not be built. The files are unchanged.',

  'file.deleteNamed': 'Delete {name}',
  'file.deleteSelected': 'Delete selected: {count}',
  'file.deleteTitle': 'Delete the selected Patient files?',
  'file.deleteKeep': 'Keep files',
  'file.deleteConfirm': 'Delete permanently',
  'file.deleting': 'Deleting…',
  'file.deleteFilesTitle': 'Files and derived copies',
  'file.deleteFilesDetail': 'The original bytes and every processed copy will be physically deleted. The audit record and your reason remain.',
  'file.deleteReason': 'Reason for deletion',
  'file.deleteReasonHint': 'Required and recorded with your name in the audit history.',
  // The predefined causes every deletion offers; "other" reveals the field for
  // the clinician's own words. What is chosen or typed is what the audit keeps.
  'file.deleteReasonWrongPatient': 'Uploaded to the wrong patient',
  'file.deleteReasonDuplicate': 'Duplicate upload',
  'file.deleteReasonMistake': 'Uploaded by mistake',
  'file.deleteReasonOther': 'Other reason',
  'file.deleteReasonDetail': 'Reason in your own words',
  'file.deleteFailed': 'Deletion could not be completed. Items already deleted will disappear; untouched items remain available.',
  /* WHAT REFUSED, AND WHICH ROW. The purges run one row at a time, so each of
     these is about the one row the dialog names beneath the sentence, and each
     leaves a different next act. */
  'file.deleteRefusedChanged': 'That row changed while this question stood open, so it was not deleted. Rows deleted before it are gone; reopen the deletion to act on what this one is now.',
  'file.deleteRefusedGone': 'That row is already gone, so there was nothing left to delete for it. Rows deleted before it are gone too.',
  'file.deleteRefusedForbidden': 'This account may not delete that row. It was not deleted; rows deleted before it are gone.',
  'file.deleteRefusedInvalid': 'The deletion was refused as stated, so that row was not deleted. Check the reason and try again; rows deleted before it are gone.',
  'file.deleteRefusedUnavailable': 'Storage did not answer for that row, so it was not deleted. Rows deleted before it are gone, and this is worth trying again.',

  'file.comment': 'Comment',
  'file.commentLoading': 'Loading details',
  'file.commentUnavailable': 'Details are unavailable',
  'file.noComment': 'No comment recorded',
  'file.mediaType': 'Stored type',
  'file.digest': 'Checksum',
  'file.noDocumentDate': 'Not recorded',
  'file.technicalTitle': 'Technical details',

  /* The panel's chip row, when a file has neither a Group nor any Content and
     the classifier is not working on it: one muted fact, no caption. The AI
     act stands beside Edit details and is named for what it does, not for how
     many times: it is offered on a file nobody has classified yet as well. */
  'file.classificationNotSet': 'No group or content',
  'file.classifyAI': 'AI classify',
  'file.classifyAIRequesting': 'Requesting',
  'file.classifyAIOverwriteTitle': 'Replace file classification?',
  'file.classifyAIOverwriteDescription':
    'AI will classify this file again and may replace the current Group and Content.',
  'file.classifyAIOverwriteCancel': 'Keep current',
  'file.classifyAIOverwriteConfirm': 'Reclassify with AI',

  /* Editing the three display facts a file has. The stale-revision copy is
     separate from every other refusal on purpose: it is the only one a
     clinician recovers from by READING what is saved now, and collapsing it
     into "could not save" leaves someone retrying a write that cannot be
     accepted until they do. */
  'file.editMetadata': 'Edit details',
  'file.editFormLabel': 'Edit details for {name}',
  'file.editTitle': 'Title',
  'file.editTitleHint': 'Leave empty to show the stored filename.',
  'file.editDocumentDate': 'Document date',
  'file.editDocumentDateHint': 'Leave empty if the document has no date.',
  'file.editComment': 'Comment',
  'file.editSave': 'Save details',
  'file.editSaving': 'Saving',
  'file.editCancel': 'Cancel',
  'file.editStaleRevision': 'Someone else changed these details while you were editing.',
  'file.editStaleRevisionDetail':
    'Load the saved revision to see what is stored now, then apply your change to it.',
  'file.editReload': 'Load the saved revision',
  // Says plainly that nothing was lost. A failed recovery must not read as a
  // second failure that also took the typed text with it.
  'file.editReloadFailed': 'The saved revision could not be loaded. Your changes are still here.',
  'file.editInvalid': 'Check the details and try again.',
  'file.editForbidden': 'You do not have permission to edit this file.',
  'file.editGone': 'This file is no longer available.',
  'file.editSessionEnded': 'Your session ended. Sign in again to continue.',
  'file.editUnavailable': 'The details could not be saved right now. Try again later.',
  'file.editUnsavedTitle': 'Unsaved details',
  'file.editUnsavedDetail': 'You have changed the details of this file and not saved them.',
  'file.editKeepEditing': 'Keep editing',
  'file.editDiscard': 'Discard changes',
  // Named in the leave prompt's list, so "you have unsaved changes" can be
  // answered by someone who knows which work it means.
  'file.unsavedIntent': 'File details',

  /* Record links. What is LINKED is stated by the file's own detail; what is
     LINKABLE is a cursor page of the patient's final records, so its emptiness
     is worded to say which of the two it is. */
  'file.linksTitle': 'EMR entries',
  'file.linksNone': 'No EMR entries are linked to this file.',
  'file.linksAdd': 'Add an EMR entry',
  'file.linkPick': 'Link to a record…',
  'file.linkExternal': 'External',
  'file.linkedAt': 'Linked {date}',
  'file.unlinkRecord': 'Unlink {name}',
  'file.unlinkAction': 'Unlink',
  'file.linkAddTitle': 'Link to a record',
  'file.linkRecord': 'Link to {name}',
  'file.linkAction': 'Link',
  'file.linkWorking': 'Working',
  'file.linkRecordsLoading': 'Loading records',
  'file.linkRecordsUnavailable': 'The record list is unavailable',
  'file.linkNoneAvailable': 'This patient has no other final records to link.',
  // NOT the sentence above: more records may exist further back, and saying
  // there are none would be a claim about the chart this page cannot support.
  'file.linkNoneOnThisPage': 'No further records on this page. Show older records to find more.',
  'file.linkMoreRecords': 'Show older records',
  'file.recordFinalizedAt': 'Finalized',
  'file.recordStatusFinal': 'Final',
  'file.linkForbidden': 'You do not have permission to change record links for this file.',
  'file.linkGone': 'That link could not be changed. The file or the record is no longer available.',
  'file.linkUnavailable': 'The record link could not be changed right now. Try again later.',

  /* Derived documents: the fixed mini-cell column, its filter, and the panel
     block. A chip's accessible name IS its whole cell — the icon inside is
     decorative — so every state of every kind is a complete sentence, and the
     downloadable states name the file the way every row control does. */
  'file.columnDerived': 'Derived',
  'file.derivedDownloadTranslation': 'Download the translation of {name}',
  'file.derivedDownloadTranslationIn': 'Download the {languages} translation of {name}',
  'file.derivedDownloadDeidentified': 'Download the anonymized copy of {name}',
  'file.derivedDownloadOcr': 'Download the recognized text of {name}',
  'file.derivedPendingTranslation': 'Translation in progress',
  'file.derivedPendingDeidentified': 'Anonymized copy in progress',
  'file.derivedPendingOcr': 'Text recognition in progress',
  'file.derivedAttentionTranslation': 'The translation cannot be served',
  'file.derivedAttentionDeidentified': 'The anonymized copy cannot be served',
  'file.derivedAttentionOcr': 'The recognized text cannot be served',
  'file.derivedNoneTranslation': 'No translation',
  'file.derivedNoneDeidentified': 'No anonymized copy',
  'file.derivedNoneOcr': 'No recognized text',
  'file.derivedFilterTranslation': 'Has a translation',
  'file.derivedFilterDeidentified': 'Has an anonymized copy',
  'file.derivedFilterOcr': 'Has recognized text',
  'file.derivedFilterNone': 'No derived documents',

  /* The panel block. Its emptiness sentences are three DIFFERENT claims:
     the file is not connected to processing at all, the documents read has
     not answered or failed, and the connected file simply has none yet. */
  'file.derivedTitle': 'Derived documents',
  'file.deriveNotConnected': 'Processing is not connected for this file.',
  'file.deriveLoading': 'Loading derived documents',
  'file.deriveUnavailable': 'Derived documents are unavailable',
  'file.deriveNone': 'No derived documents yet.',
  'file.derivedKindTranslation': 'Translation',
  'file.derivedKindInLanguage': '{kind} ({language})',
  'file.derivedKindDeidentified': 'Anonymized copy',
  'file.derivedKindOcr': 'Recognized text',
  'file.deriveDownload': 'Download',
  'file.deriveStatePending': 'Processing',
  // Deliberate cleanup, never a failure: the copy was removed on purpose and
  // the tombstone line is the record that it existed.
  'file.deriveStatePurged': 'Deleted',
  'file.deriveStateBroken': 'The stored copy cannot be served',
  // The card is still the old copy and still downloadable; the work replacing
  // it is under way. Not "processing", which is what a NEW copy says.
  'file.deriveStateRecreating': 'Being made again',
  'file.deriveStateFailed': 'The latest attempt failed',
  'file.deriveRetry': 'Retry',
  'file.deriveRetryNamed': 'Retry {name}',
  'file.deriveMadeByProcessing': 'Open the processing',
  'file.deriveExtractEntries': 'Extract EMR entries…',
  'file.derivePurge': 'Delete copy',
  'file.derivePurgeNamed': 'Delete the stored copy of {name}',
  'file.derivePurgeConfirm': 'Delete this stored copy?',
  'file.derivePurgeYes': 'Delete',
  'file.derivePurgeKeep': 'Keep',
  'file.deriveRecreateNamed': 'Recreate {name}',
  'file.deriveAdd': 'Add a derived document',
  'file.deriveAddSummary': 'Summary',
  'file.deriveSourceTitle': 'Source',
  'file.deriveSourceOriginal': 'From the original',
  // Said where the anonymized copy already exists: the run READS that copy,
  // and nothing is anonymized a second time.
  'file.deriveSourceDeidentified': 'From the anonymized copy',
  'file.deriveDeidentify': 'Anonymize the source first',
  'file.runStatusQueued': 'Queued',
  'file.runStatusRunning': 'Processing',
  'file.runStatusRetryWait': 'Retrying',
  'file.runStatusSucceeded': 'Done',
  'file.runStatusPartiallySucceeded': 'Partially done',
  'file.runStatusFailed': 'Failed',
  'file.runStatusCanceled': 'Canceled',
  'file.deriveCancel': 'Cancel processing',
  'file.deriveCancelRequested': 'Canceling',
  'file.deriveRunFailure': 'Processing failed',
  'file.failure.canceled': 'Canceled.',
  'file.failure.dependency': 'An earlier step of this processing failed.',
  'file.failure.formatUnsupported': 'The file cannot be opened: the format is not supported, or the file is damaged or too large.',
  'file.failure.noText': 'No readable text was found in this file.',
  'file.failure.notConfigured': 'The processing service is not set up for the clinic.',
  'file.failure.serviceFailed': 'The processing service failed; a retry may help.',
  'file.failure.integrity': 'The stored file does not match its checksum.',
  'file.failure.tooLarge': 'The file is too large to process.',
  'file.failure.provider': 'The AI service did not answer; a retry may help.',
  'file.failure.resultInvalid': 'The AI service returned an unusable result; a retry may help.',
  'file.failure.aiUnavailable': 'This AI function is off in the clinic or has no key.',
  'file.failure.unknown': 'The processing did not finish.',
  /* Refusal sentences follow the house rule: say what did not happen and that
     the stored data is unchanged. */
  'file.deriveStartFailed': 'Processing could not be started. The file is unchanged.',
  'file.deriveCancelFailed': 'The cancellation could not be delivered. The run continues.',
  'file.derivePurgeFailed':
    'The stored copy could not be deleted. It may still be feeding a run in progress.',
  'file.deriveDownloadFailed': 'The copy could not be downloaded. The stored data is unchanged.',

  /* The processing panel over the ticked rows: what was selected, what can be
     done with it, and the act that starts it. */
  'file.processOne': 'Process 1 file',
  'file.processSelected': 'Process {count} files',
  'file.processTitle': 'Process files',
  'file.processSelection': 'Selected',
  'file.processSelectedFolders': 'Folders',
  'file.processSelectedFiles': 'Files',
  'file.processSelectedStudies': 'Studies',
  'file.processFoldersIncluded': 'The files in the selected folders and all their subfolders are included.',
  'file.processActions': 'What to do',
  'file.processEntries': 'Extract EMR entries',
  'file.processEntriesHint': 'AI proposes an entry for each episode of care; you review every one before it is saved.',
  'file.processSummarize': 'Summarize',
  'file.processSummarizeHint': 'One summary across all the files, in the chosen language.',
  'file.processTranslate': 'Translate',
  'file.processTranslateHint': 'A translated copy of each file, in the chosen language.',
  'file.processOcr': 'Recognize text',
  'file.processOcrHint': 'The text of scans and photos, for search and reading.',
  'file.processDeidentify': 'Anonymize',
  'file.processDeidentifyHint': 'A PDF copy of each file without the patient’s personal details.',
  'file.processLanguage': 'Language of the result',
  'file.processChoose': 'Choose what to do with the files.',
  'file.processStart': 'Start',
  'file.processStarting': 'Starting…',
  'file.processNotReady': 'Not ready for processing yet',
  'file.processNone': 'None of the selected files can be processed yet.',
  'file.processSteps': 'Steps done: {done} of {total}',
  /* THE SHARED ADD TAKES A WORD only while the imaging Add stands beside it —
     two orange controls that both said Add would be a coin toss. */
  'file.addFile': 'Add file',
  'file.addFolder': 'Add folder',
  'file.foldersOpen': 'Folder tree',
  'file.foldersTitle': 'Folders',
  'file.foldersSearch': 'Find a folder or file',
  'file.foldersSearchPlaceholder': 'Search folders and files',
  'file.foldersShowEverything': 'Everything',
  'file.foldersShowFolders': 'Folders only',
  'file.foldersShowFiles': 'Files only',
  'file.foldersLoading': 'Reading the folders…',
  'file.foldersFilesLoading': 'Reading the files…',
  'file.foldersUnavailable': 'The folders could not be read.',
  'file.foldersEmpty': 'No folders yet. Everything is listed in one place until a folder is made.',
  'file.foldersEmptyAdd':
    'No folders yet. Everything is listed in one place until a folder is made. “Add a folder”, at the top of the panel, makes the first one.',
  'file.foldersNoMatches': 'No folder or file matches.',
  'file.foldersLevelEmpty': 'Nothing is filed here yet.',
  'file.foldersLevelFilesOnly': 'Only files are filed here, and the fold shows folders.',
  'file.foldersAll': 'All files',
  'file.foldersUnfiled': 'No folder',
  'file.foldersNewName': 'New folder',
  'file.foldersAddTop': 'Add a folder',
  'file.foldersAddHere': 'Add a folder inside',
  'file.foldersRename': 'Rename',
  'file.foldersMoveEarlier': 'Move up',
  'file.foldersMoveLater': 'Move down',
  'file.foldersMoveToTop': 'Move to the top level',
  'file.foldersDelete': 'Delete folder',
  'file.foldersOpenFile': 'Open',
  'file.foldersUnfile': 'Take out of the folder',
  'file.foldersMenu': 'Actions for {name}',
  'file.foldersDeleteTitle': 'Delete “{name}”?',
  'file.foldersDeleteDescription': 'The folder and the folders inside it are removed. Their files and studies stay in the chart, without a folder.',
  'file.foldersDeleteCancel': 'Keep',
  'file.foldersDeleteConfirm': 'Delete',
  'file.foldersDeleting': 'Deleting…',
  'file.foldersStale': 'The folders were changed elsewhere and have been reread. Try again.',
  'file.foldersNameExists': 'A folder with this name already stands here.',
  'file.foldersCycle': 'A folder cannot be moved inside itself.',
  'file.foldersDepth': 'Folders cannot nest any deeper here.',
  'file.foldersForbidden': 'Your account may not change the folders.',
  'file.foldersFailed': 'The folders could not be changed.',
  'file.foldersRoot': 'Root',
  'file.foldersUpTo': 'Up to {name}',
  'file.folderOpenNamed': 'Open the folder {name}',
  'file.columnFolder': 'Folder',
  'file.locationLabel': 'Where you are in the folders',
  'file.locationBack': 'Back',
  'file.locationForward': 'Forward',
  'file.locationUp': 'Up one folder',
  'file.folderNewKey': 'New folder (F7)',
  'file.searchDeep': 'Search in all nested folders',
  'file.folderCreateTitle': 'New folder',
  'file.folderRenameTitle': 'Rename the folder',
  'file.folderNameLabel': 'Folder name',
  'file.folderNameCancel': 'Cancel',
  'file.folderCreateConfirm': 'Create',
  'file.folderRenameConfirm': 'Rename',
  'file.folderRenameNamed': 'Rename the folder {name}',
  'file.folderDeleteNamed': 'Delete the folder {name}',
  'file.folderHolds': 'Folders: {folders}, files: {files}',
  'file.folderHoldsEmpty': 'Empty',
  'file.folderEmpty': 'This folder is empty',
  'file.folderEmptyDetail': 'Drag files here, or upload them while this folder is open.',
  'file.menuOpen': 'Open',
  'file.menuProperties': 'Properties',
  'file.menuDownload': 'Download',
  'file.menuRename': 'Rename',
  'file.menuMove': 'Move to a folder…',
  'file.menuDelete': 'Delete',
  'file.menuSelected': 'Actions for the selected: {count}',
  'file.deleteFoldersTitle': 'Folders with everything inside',
  'file.deleteFoldersDetail': 'Each folder is deleted together with the folders inside it, and every file and study inside is deleted physically, as described below.',
  'file.deleteFoldersReading': 'Reading what the folders hold…',
  'file.deleteFoldersUnread': 'What the folders hold could not be read, so they cannot be deleted now.',
  'file.deleteFoldersBlocked': 'Inside are items you may not delete, or that cannot be deleted yet: {count}. Take them out of the folders first.',
  'file.downloadGathering': 'Reading what the ticked folders hold…',
  'file.foldersContentsUnread': 'What the ticked folders hold could not be read, so they cannot be downloaded or processed now.',
  'file.foldersContentsRetry': 'Read again',
  'file.moveSelected': 'Move to folder: {count}',
  'file.moveSelectedTitle': 'Move to a folder',
  'file.moveSelectedDescription': 'Selected for filing: {count}. Choose where they go.',
  'file.moveSelectedTarget': 'Folder',
  'file.moveSelectedUnfiled': 'No folder',
  'file.moveSelectedSkipped': 'Left out: {count} — still arriving or not yet registered, so they cannot be filed.',
  'file.moveSelectedCancel': 'Cancel',
  'file.moveSelectedConfirm': 'Move {count}',
  'file.opTypeDeidentify': 'Anonymization',
  'file.opTypeOcr': 'Text recognition',
  'file.opTypeTranslate': 'Translation',
  'file.opTypeSummarize': 'Summary',
  'file.opTypeRender': 'Document rendering',
  'file.opTypePurge': 'Cleanup',

  'upload.title': 'Add a medical file',
  'upload.titleBundle': 'Add medical files',
  // The SERVER's limit, stated before a file is chosen rather than after one is
  // refused. The size is formatted by the same helper the chosen-file line uses.
  'upload.limit': 'Files up to {size} are accepted.',
  'upload.filesSelected': '{count} files selected',
  /* WHAT A PARTLY ACCEPTED BUNDLE IS NOW. Both counts, because the pick alone
     has stopped describing what the next press sends, and the reason the
     stored ones are gone from the list — they are safe, not forgotten.
     Label-and-count rather than a sentence: one file and nine read the same
     way in every catalogue, and this product has no plural machinery. */
  'upload.filesPartial':
    'Already stored: {stored}. Still to upload: {count}. The stored files are not sent again.',
  'upload.progressLabel': 'Upload progress',
  // Honest about what it does NOT know. Nothing here can observe bytes sent, so
  // nothing here shows a share.
  'upload.progressIndeterminate': 'Uploading. How much has been sent cannot be shown.',
  'upload.progressIndeterminateBundle': 'Uploading {count} files. How much has been sent cannot be shown.',
  'upload.unsavedIntent': 'A file upload',
  'upload.fieldTitle': 'Title',
  'upload.fieldServiceDate': 'Clinical service date',
  'upload.fieldFile': 'File',
  'upload.fieldFolder': 'Folder',
  'upload.folderUnfiled': 'No folder',
  // The checkbox's own line says what it fills and when; there is no hint
  // under it. A Group or Content the clinician chose above stays theirs.
  'upload.aiClassification': 'Let AI choose the group and content after upload',
  'upload.submit': 'Upload',
  'upload.submitting': 'Uploading',
  // Named for the surface beside the form. The upload lands in the files
  // registry the clinician is looking at, not in a chronology this card shows.
  'upload.accepted': 'The file was accepted and added to this patient’s files.',
  'upload.acceptedBundle': '{count} files were accepted and added to this patient’s files.',
  // The details section's own heading and the one sentence that states the
  // rule: every field in it is optional, in any combination including none.
  'upload.detailsTitle': 'Details',
  'upload.detailsHint': 'Optional — fill in any of these, or none.',
  'upload.detailsBundleHint':
    'Optional — fill in any of these, or none. Filled details apply to every selected file.',
  /* The upload panel's own cancel and its own discard question. A chosen file
     with typed details is work that exists only in this browser, so the
     destructive control names what it does rather than agreeing with the
     sentence above it. */
  'upload.discardTitle': 'File not uploaded',
  'upload.discardBundleTitle': 'Files not uploaded',
  'upload.discardDetail': 'You have started adding this file and have not uploaded it.',
  'upload.discardBundleDetail': 'You have started adding these files and have not uploaded them.',
  // The same question once part of the bundle is stored: only the files still
  // listed are being given up, and saying otherwise overstates what is lost.
  'upload.discardRemainingDetail':
    'The files still listed have not been uploaded. Files already stored: {count} — they stay in this patient’s files.',
  'upload.keepEditing': 'Keep editing',
  /* LEAVING WHILE THE BUNDLE IS STILL RUNNING is a different question from
     discarding one: nothing is cancelled by closing, and what is actually lost
     is the panel's answer for the files still in flight — including a refusal,
     which would otherwise fail in silence. */
  'upload.leavingTitle': 'Upload still running',
  'upload.leavingDetail':
    'The files still being sent will finish and appear in this patient’s files. Closing stops nothing, but what each of them came to is no longer shown here.',
  'upload.leavingWait': 'Wait here',
  'upload.leavingConfirm': 'Close anyway',
  'upload.discardConfirm': 'Discard this file',
  'upload.discardBundleConfirm': 'Discard these files',
  /* THE FOOT'S ANSWER FOR A BUNDLE, which is a count and a direction rather
     than a reason: the reasons differ per file and are written on the rows,
     where the name the clinician has to act on already is. */
  'upload.bundleRefused':
    'Files not accepted: {count} of {total}. The reason is on each file above.',
  'upload.unsupportedType': 'That file cannot be uploaded as a binary file.',
  'upload.tooLarge': 'That file is larger than 300 MB.',
  'upload.tooLargeUnstated': 'That file is too large to upload.',
  'upload.conflict': 'This upload was already submitted with different details. Start a new upload.',
  'upload.invalidInput': 'Check the details and try again.',
  'upload.sessionEnded': 'Your session ended. Sign in again to upload.',
  'upload.forbidden': 'You do not have permission to add files for this patient.',
  'upload.patientGone': 'This patient is no longer available.',
  'upload.unavailable': 'The file could not be uploaded right now. Try again later.',


  /* The classifier's state, as a chip in the panel's row and a line under
     the registry's Group cell: waiting, working again on values already
     shown, or needing a person. */
  'file.classificationPending': 'AI classification pending',
  'file.classificationFailed': 'AI classification needs attention',
  'file.classificationReclassifying': 'Being classified again',

  /* ---------------------------------------------------------------------
     THE PATIENT EMR JOURNAL. One row per ENCOUNTER, never per Record: two
     protocol Records inside one encounter are one journal entry, and the server
     says so. The copy here must not describe rows as notes, or a clinician
     reading "3 entries" would be counting the wrong thing. */
  'emrJournal.title': 'EMR journal',
  'emrJournal.tableLabel': 'Patient EMR journal',
  'emrJournal.loading': 'Loading the EMR journal',
  'emrJournal.loadFailed': 'The EMR journal could not be loaded',
  'emrJournal.forbidden': 'You do not have permission to view EMR records',
  'emrJournal.forbiddenDetail': 'The rest of the patient card is still available.',
  'emrJournal.empty': 'No EMR entries yet',
  'emrJournal.emptyDetail': 'Encounters recorded for this patient will appear here.',
  // A filtered-empty result is a DIFFERENT fact from an empty chart, and the
  // action that resolves it is different too.
  'emrJournal.emptyFiltered': 'No EMR entries match these filters',
  'emrJournal.emptyFilteredDetail': 'Clear the filters to read the whole journal.',
  // The SERVER's count over the whole result; a page carries at most `limit`.
  'emrJournal.countOne': '{count} entry',
  'emrJournal.countMany': '{count} entries',
  'emrJournal.columnStarted': 'Date and time',
  'emrJournal.columnType': 'Entry type',
  'emrJournal.columnPractitioner': 'Practitioner',
  'emrJournal.columnStatus': 'Status',
  'emrJournal.columnCase': 'Case',
  'emrJournal.columnSections': 'Sections',
  'emrJournal.typeScheduled': 'Scheduled encounter',
  'emrJournal.typeUnscheduled': 'Unscheduled encounter',
  'emrJournal.externalManual': 'Entered manually',
  'emrJournal.externalAI': 'From documents (AI)',
  'emrJournal.externalNoDate': 'Date not set',
  // THREE CASE STATES, as everywhere else in this product: a recorded Case, an
  // explicit decision not to group, and an encounter from before the question
  // was asked. The last must not imply anybody decided anything.
  'emrJournal.sectionProtocol': 'Protocol',
  'emrJournal.sectionDiagnoses': 'Diagnoses',
  'emrJournal.sectionAbsent': 'Not started',
  'emrJournal.sectionIncomplete': 'Incomplete',
  'emrJournal.sectionComplete': 'Complete',
  'emrJournal.sectionState': '{section}: {state}',
  'emrJournal.sectionFiles': 'Files ({count})',
  // FILTERED BY THE SERVER. Every control in the column funnels maps to a
  // query parameter the endpoint owns, so the table never post-filters a page
  // it was handed.
  'emrJournal.filterFrom': 'Started from',
  'emrJournal.filterTo': 'Started to',
  // The first column shows two facts, so its one criterion box matches both.
  'emrJournal.criterionStarted': 'Matched against the start time as shown and the Encounter code.',
  'emrJournal.criterionCase': 'Matched against the Case code and its title.',
  'emrJournal.caseFacetOption': '{code} — {title}',
  'emrJournal.filterEnteredInError': 'Show entered in error',

  /* ---------------------------------------------------------------------
     ADD EMR ENTRY. The request creates the Case when needed, the Encounter and
     the preliminary primary protocol in one server transaction. */
  'emrEntry.title': 'Add EMR entry',
  'emrEntry.branch': 'Place of care',
  'emrEntry.workplaceRequired': 'Select a workplace',
  'emrEntry.workplaceUnavailable': 'This workplace changed or is no longer available. Select an available workplace again.',
  'emrEntry.workplace': 'Workplace',
  'emrEntry.workplaceNone': 'not chosen',
  'emrEntry.workplaceChange': 'Change',
  'emrEntry.workplaceChangeLabel': 'Change workplace',
  'emrEntry.workplaceChoose': 'Choose workplace',
  'emrEntry.branchRequired': 'Select a branch',
  'emrEntry.branchUnavailable': 'This branch is no longer available. Select another branch.',
  'emrEntry.detail': 'A new encounter and its primary protocol are created together.',
  'emrEntry.placeLegend': 'Where the care took place',
  'emrEntry.placeClinic': 'In our clinic',
  'emrEntry.placeClinicHint': 'Starts an encounter with its primary protocol under a Case.',
  'emrEntry.placeOutside': 'At another clinic',
  'emrEntry.placeOutsideHint': 'Records care the patient received elsewhere, on the external entry protocol.',
  'emrEntry.externalDetail': 'A draft external entry is created and opens: write it there and complete it.',
  'emrEntry.externalProtocolFixed': 'Care received elsewhere is always recorded on this protocol.',
  'emrEntry.caseLegend': 'Case',
  'emrEntry.caseNoneOpen': 'This patient has no open case, so this entry starts a new one.',
  'emrEntry.caseChoose': 'Choose the case this entry belongs to.',
  'emrEntry.submit': 'Create entry',
  'emrEntry.submitting': 'Creating…',
  'emrEntry.failed': 'The EMR entry could not be created. Try again.',
  'emrEntry.forbidden': 'You do not have permission to add an EMR entry.',
  'emrEntry.unavailable': 'The service is unavailable. Try again shortly.',
  'emrEntry.conflict': 'The EMR entry was not created. Reload the journal and try again.',
  'emrEntry.caseUnavailable': 'That case can no longer take a new entry. Choose another, or create one.',
  'emrEntry.gone': 'This patient is no longer available.',
  'emrEntry.sessionEnded': 'Your session ended. Sign in again to continue.',

  /* ---------------------------------------------------------------------
     A FILE'S GROUP AND CONTENT. The Group is one word for what kind of
     document this is; the Content is what is in it, several words at most.
     Each value wears a mark saying who chose it — a person, the AI, the AI
     with a person's confirmation, the system — and the mark's sentence is
     about the SELECTION, never about who wrote the document or whether it
     is right. A draft sentence ends by saying it is not saved: the editor
     previews the state a Save would record.
     --------------------------------------------------------------------- */
  'file.group': 'Group',
  'file.content': 'Content',
  'file.groupUnchosen': 'Choose a group',
  'file.contentUnchosen': 'Choose content',
  'provenance.human': 'Chosen by a person',
  'provenance.humanDraft': 'Chosen by you. Not saved yet.',
  'provenance.ai': 'Chosen by AI',
  'provenance.aiConfirmed': 'Chosen by AI, confirmed by a person',
  'provenance.aiConfirmedDraft': 'Chosen by AI, confirmed by you. Not saved yet.',
  'provenance.system': 'Set by the system',
  'provenance.unknown': 'Origin not recorded',
  'fileGroup.medical': 'Medical',
  'fileGroup.financial': 'Financial',
  'fileGroup.identity': 'Identity',
  'fileGroup.administrative': 'Administrative',
  'fileGroup.collection': 'Document collection',
  'fileGroup.other': 'Other',
  'fileContent.medical_conclusion': 'Medical conclusion',
  'fileContent.discharge_summary': 'Discharge summary',
  'fileContent.clinical_referral': 'Referral',
  'fileContent.laboratory_results': 'Laboratory results',
  'fileContent.study_report': 'Diagnostic report',
  'fileContent.medical_images': 'Medical images',
  'fileContent.prescription': 'Prescription',
  'fileContent.treatment_plan': 'Treatment plan',
  'fileContent.vaccination_record': 'Vaccination record',
  'fileContent.medical_summary': 'Medical summary',
  'fileContent.invoice': 'Invoice',
  'fileContent.treatment_cost_estimate': 'Treatment cost estimate',
  'fileContent.payment_guarantee': 'Payment guarantee',
  'fileContent.payment_receipt': 'Payment receipt',
  'fileContent.insurance_policy': 'Insurance policy',
  'fileContent.passport': 'Passport',
  'fileContent.identity_card': 'Identity card',
  'fileContent.driving_licence': 'Driving licence',
  'fileContent.residence_permit': 'Residence permit',
  'fileContent.agreement': 'Agreement',
  'fileContent.consent': 'Consent',
  'fileContent.appointment_confirmation': 'Appointment confirmation',
  'fileContent.travel_booking': 'Tickets and bookings',
  'fileContent.correspondence': 'Correspondence',
} as const
